import { useState, useEffect } from 'react';
import { SoftwareCard } from '@/components/SoftwareCard';
import { SubmissionForm } from '@/components/SubmissionForm';
import { PaginationControls } from '@/components/PaginationControls';
import { AnniversaryHighlight } from '@/components/AnniversaryHighlight';
import { obsoleteSoftware } from '@/data/obsoleteSoftware';
import heroImage from '@/assets/hero-memorial.jpg';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

const Index = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Find all software with anniversary today
  const today = new Date();
  const todayMonth = today.getMonth();
  const todayDay = today.getDate();

  const anniversarySoftwareList = obsoleteSoftware.filter((software) => {
    const endDate = new Date(software.endDate);
    return endDate.getMonth() === todayMonth && endDate.getDate() === todayDay;
  });

  const filteredSoftware = obsoleteSoftware.filter(
    (software) =>
      software.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      software.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredSoftware.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSoftware = filteredSoftware.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="bg-gradient-memorial min-h-screen">
      {/* Hero Section */}
      <div className="relative">
        <div
          className="h-240 bg-cover bg-center bg-no-repeat md:h-280"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="bg-memorial-bg/80 absolute inset-0 backdrop-blur-sm" />
          <div className="relative z-10 flex h-full items-start justify-center pt-12 md:pt-20">
            <div className="space-y-8 px-4 text-center">
              <div className="space-y-2">
                <div className="text-memorial-muted/40 text-6xl font-bold tracking-wider md:text-8xl">
                  R.I.P
                </div>
                <h1 className="text-memorial-text text-5xl font-bold md:text-7xl">
                  <span className="text-cyber-glow animate-cyber-glow">GraveHub</span> Memorial
                </h1>
              </div>
              <p className="text-memorial-muted mx-auto max-w-3xl text-xl md:text-2xl">
                Honoring the software that shaped our digital world. From groundbreaking innovations
                to beloved tools that are no longer with us.
              </p>
              <div className="animate-pulse-glow">
                <div className="border-cyber-glow/30 bg-cyber-glow/10 inline-block rounded-lg border px-4 py-2">
                  <span className="text-cyber-glow font-mono">
                    {obsoleteSoftware.length} software remembered
                  </span>
                </div>
              </div>
            </div>
          </div>

          <AnniversaryHighlight software={anniversarySoftwareList} />
        </div>
      </div>

      {/* Submit Button */}
      <div className="container mx-auto px-4 pb-12">
        <div className="text-center">
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-cyber-glow text-primary-foreground hover:bg-cyber-glow/80 hover:shadow-cyber mt-16 px-8 py-6 text-lg">
                Submit Memorial to GraveHub
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gradient-card border-memorial-border text-memorial-text max-w-2xl">
              <VisuallyHidden.Root>
                <DialogTitle>Submit Software to GraveHub</DialogTitle>
              </VisuallyHidden.Root>
              <SubmissionForm />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="mx-auto mb-12 max-w-2xl">
          <div className="relative">
            <input
              type="text"
              placeholder="Search the memorial..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-memorial-card border-memorial-border text-memorial-text placeholder:text-memorial-muted focus:border-cyber-glow focus:shadow-glow w-full rounded-lg border px-4 py-3 transition-all duration-300 focus:outline-none"
            />
            <div className="text-memorial-muted absolute top-1/2 right-3 -translate-y-1/2 transform">
              🔍
            </div>
          </div>
        </div>

        {/* Pagination Controls */}
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={filteredSoftware.length}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
          }}
        />

        {/* Software Grid */}
        <div className="mb-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedSoftware.map((software, index) => (
            <div
              key={software.id}
              className="animate-fade-in-up"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <SoftwareCard software={software} />
            </div>
          ))}
        </div>

        {/* Bottom Pagination */}
        {totalPages > 1 && (
          <div className="mb-16">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={filteredSoftware.length}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
              showItemsPerPage={false}
            />
          </div>
        )}

        {/* No Results */}
        {filteredSoftware.length === 0 && searchTerm && (
          <div className="py-12 text-center">
            <div className="mb-4 text-6xl">🔍</div>
            <h3 className="text-memorial-text mb-2 text-xl">No software found</h3>
            <p className="text-memorial-muted">
              Try a different search term or use the Submit Software button to add it.
            </p>
          </div>
        )}

        {/* Footer */}
        <footer className="border-memorial-border mt-16 border-t py-8 text-center">
          <p className="text-memorial-muted">
            In memory of all the software that made computing history.{'  '}
            <span className="text-cyber-glow">Never forgotten</span>. {'- '}
            <a href="mailto:victor@cerneaconsulting.com">Contact us</a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Index;
